import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL2 } from "./service/apiService";

const MealsReport = () => {
  const [report, setReport] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [filterSiteId, setFilterSiteId] = useState("");
  const [filterGroup, setFilterGroup] = useState("");
  const [activeTab, setActiveTab] = useState("siteWise");

  const fetchSites = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL2}/meals_sites.php`);
      if (res.data.status) setSites(res.data.data);
    } catch (err) {
      console.error("Error fetching sites:", err);
    }
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {};
      if (filterStartDate && filterEndDate) {
        payload.start_date = filterStartDate;
        payload.end_date = filterEndDate;
      }
      if (filterSiteId) payload.site_id = filterSiteId;
      if (filterGroup) payload.kitchen_group = filterGroup;

      const res = await axios.post(`${API_BASE_URL2}/meals_report.php`, payload);
      if (res.data.status) {
        setReport(res.data);
      }
    } catch (err) {
      console.error("Error fetching report:", err);
    }
    setLoading(false);
  }, [filterStartDate, filterEndDate, filterSiteId, filterGroup]);

  useEffect(() => {
    fetchSites();
    fetchReport();
  }, [fetchSites, fetchReport]);

  const uniqueGroups = [...new Set(sites.map((s) => s.kitchen_group).filter(Boolean))];

  const formatNum = (n) => {
    const num = parseInt(n) || 0;
    return num.toLocaleString("en-IN");
  };

  return (
    <div className="content">
      <div className="container-fluid">
        <div className="row g-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center bg-white">
                <h5 className="mb-0">
                  <i className="bi bi-bar-chart me-2"></i>Meals Report
                </h5>
                <Link to="/MealsHome" className="btn btn-outline-primary btn-sm">
                  <i className="bi bi-arrow-left me-1"></i>Back to Meals
                </Link>
              </div>
              <div className="card-body">
                {/* Filters */}
                <div className="row g-2 mb-3">
                  <div className="col-md-2">
                    <label className="form-label small">From Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={filterStartDate}
                      onChange={(e) => setFilterStartDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small">To Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={filterEndDate}
                      onChange={(e) => setFilterEndDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small">Site</label>
                    <select
                      className="form-select form-select-sm"
                      value={filterSiteId}
                      onChange={(e) => setFilterSiteId(e.target.value)}
                    >
                      <option value="">All Sites</option>
                      {sites.map((s) => (
                        <option key={s.site_id} value={s.site_id}>{s.site_name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small">Kitchen Group</label>
                    <select
                      className="form-select form-select-sm"
                      value={filterGroup}
                      onChange={(e) => setFilterGroup(e.target.value)}
                    >
                      <option value="">All Groups</option>
                      {uniqueGroups.map((g, i) => (
                        <option key={i} value={g}>{g}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2 d-flex align-items-end gap-2">
                    <button className="btn btn-primary btn-sm" onClick={fetchReport}>
                      <i className="bi bi-search me-1"></i>Search
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => {
                        setFilterStartDate("");
                        setFilterEndDate("");
                        setFilterSiteId("");
                        setFilterGroup("");
                      }}
                    >
                      Reset
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary"></div>
                    <p className="mt-2">Loading report...</p>
                  </div>
                ) : report ? (
                  <>
                    {/* Summary Cards */}
                    <div className="row g-3 mb-4">
                      <div className="col-md-2">
                        <div className="card bg-primary text-white h-100">
                          <div className="card-body text-center p-3">
                            <div className="fs-3 fw-bold">{formatNum(report.summary?.total_meals)}</div>
                            <small>Total Meals</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="card bg-info text-white h-100">
                          <div className="card-body text-center p-3">
                            <div className="fs-3 fw-bold">{formatNum(report.summary?.total_breakfast)}</div>
                            <small>Breakfast</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="card bg-success text-white h-100">
                          <div className="card-body text-center p-3">
                            <div className="fs-3 fw-bold">{formatNum(report.summary?.total_lunch)}</div>
                            <small>Lunch</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="card bg-warning text-dark h-100">
                          <div className="card-body text-center p-3">
                            <div className="fs-3 fw-bold">{formatNum(report.summary?.total_dinner)}</div>
                            <small>Dinner</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="card bg-secondary text-white h-100">
                          <div className="card-body text-center p-3">
                            <div className="fs-3 fw-bold">{formatNum(report.summary?.total_sites)}</div>
                            <small>Sites</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="card bg-dark text-white h-100">
                          <div className="card-body text-center p-3">
                            <div className="fs-3 fw-bold">{formatNum(report.summary?.total_days)}</div>
                            <small>Days</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tabs */}
                    <ul className="nav nav-tabs mb-3">
                      <li className="nav-item">
                        <button
                          className={`nav-link ${activeTab === "siteWise" ? "active" : ""}`}
                          onClick={() => setActiveTab("siteWise")}
                        >
                          Site Wise
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link ${activeTab === "dateWise" ? "active" : ""}`}
                          onClick={() => setActiveTab("dateWise")}
                        >
                          Date Wise
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link ${activeTab === "groupWise" ? "active" : ""}`}
                          onClick={() => setActiveTab("groupWise")}
                        >
                          Kitchen Group Wise
                        </button>
                      </li>
                    </ul>

                    {/* Site Wise Table */}
                    {activeTab === "siteWise" && (
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover table-sm">
                          <thead className="table-dark">
                            <tr>
                              <th>#</th>
                              <th>Site Name</th>
                              <th>Kitchen Group</th>
                              <th className="text-center">Breakfast</th>
                              <th className="text-center">Lunch</th>
                              <th className="text-center">Dinner</th>
                              <th className="text-center">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {report.siteWise?.map((r, i) => (
                              <tr key={i}>
                                <td>{i + 1}</td>
                                <td>{r.site_name}</td>
                                <td><small className="text-muted">{r.kitchen_group}</small></td>
                                <td className="text-center">{formatNum(r.breakfast)}</td>
                                <td className="text-center">{formatNum(r.lunch)}</td>
                                <td className="text-center">{formatNum(r.dinner)}</td>
                                <td className="text-center fw-bold">{formatNum(r.total)}</td>
                              </tr>
                            ))}
                            {report.siteWise?.length > 0 && (
                              <tr className="table-dark fw-bold">
                                <td colSpan="3">Grand Total</td>
                                <td className="text-center">
                                  {formatNum(report.siteWise.reduce((a, r) => a + parseInt(r.breakfast || 0), 0))}
                                </td>
                                <td className="text-center">
                                  {formatNum(report.siteWise.reduce((a, r) => a + parseInt(r.lunch || 0), 0))}
                                </td>
                                <td className="text-center">
                                  {formatNum(report.siteWise.reduce((a, r) => a + parseInt(r.dinner || 0), 0))}
                                </td>
                                <td className="text-center">
                                  {formatNum(report.siteWise.reduce((a, r) => a + parseInt(r.total || 0), 0))}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Date Wise Table */}
                    {activeTab === "dateWise" && (
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover table-sm">
                          <thead className="table-dark">
                            <tr>
                              <th>#</th>
                              <th>Date</th>
                              <th className="text-center">Breakfast</th>
                              <th className="text-center">Lunch</th>
                              <th className="text-center">Dinner</th>
                              <th className="text-center">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {report.dateWise?.map((r, i) => (
                              <tr key={i}>
                                <td>{i + 1}</td>
                                <td>{r.meal_date}</td>
                                <td className="text-center">{formatNum(r.breakfast)}</td>
                                <td className="text-center">{formatNum(r.lunch)}</td>
                                <td className="text-center">{formatNum(r.dinner)}</td>
                                <td className="text-center fw-bold">{formatNum(r.total)}</td>
                              </tr>
                            ))}
                            {report.dateWise?.length > 0 && (
                              <tr className="table-dark fw-bold">
                                <td colSpan="2">Grand Total</td>
                                <td className="text-center">
                                  {formatNum(report.dateWise.reduce((a, r) => a + parseInt(r.breakfast || 0), 0))}
                                </td>
                                <td className="text-center">
                                  {formatNum(report.dateWise.reduce((a, r) => a + parseInt(r.lunch || 0), 0))}
                                </td>
                                <td className="text-center">
                                  {formatNum(report.dateWise.reduce((a, r) => a + parseInt(r.dinner || 0), 0))}
                                </td>
                                <td className="text-center">
                                  {formatNum(report.dateWise.reduce((a, r) => a + parseInt(r.total || 0), 0))}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Group Wise Table */}
                    {activeTab === "groupWise" && (
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover table-sm">
                          <thead className="table-dark">
                            <tr>
                              <th>#</th>
                              <th>Kitchen Group</th>
                              <th className="text-center">Breakfast</th>
                              <th className="text-center">Lunch</th>
                              <th className="text-center">Dinner</th>
                              <th className="text-center">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {report.groupWise?.map((r, i) => (
                              <tr key={i}>
                                <td>{i + 1}</td>
                                <td>{r.kitchen_group || "N/A"}</td>
                                <td className="text-center">{formatNum(r.breakfast)}</td>
                                <td className="text-center">{formatNum(r.lunch)}</td>
                                <td className="text-center">{formatNum(r.dinner)}</td>
                                <td className="text-center fw-bold">{formatNum(r.total)}</td>
                              </tr>
                            ))}
                            {report.groupWise?.length > 0 && (
                              <tr className="table-dark fw-bold">
                                <td colSpan="2">Grand Total</td>
                                <td className="text-center">
                                  {formatNum(report.groupWise.reduce((a, r) => a + parseInt(r.breakfast || 0), 0))}
                                </td>
                                <td className="text-center">
                                  {formatNum(report.groupWise.reduce((a, r) => a + parseInt(r.lunch || 0), 0))}
                                </td>
                                <td className="text-center">
                                  {formatNum(report.groupWise.reduce((a, r) => a + parseInt(r.dinner || 0), 0))}
                                </td>
                                <td className="text-center">
                                  {formatNum(report.groupWise.reduce((a, r) => a + parseInt(r.total || 0), 0))}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-muted text-center py-3">No report data available.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MealsReport;
